import React, { useState } from 'react';

import { Badge, Button, Col, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Container from 'components/shared/Container';
import {
  BRANDS_FOR_DROPDOWN,
  FILE_EXPORTS_UNMAPPED_GMV_PRODUCTS,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  UNMAPPED_GMV_PRODUCTS,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationBar from 'components/shared/PaginationBar';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import { SwalToast } from 'lib/utils/toast';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const PAGE_SIZE = 100;

function ProductsWithASaleIn1MAndUnmappedView() {
  const hasUserPermissions = useUserPermissions();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const offset = currentPage * PAGE_SIZE;

  const { data, loading } = useGet(UNMAPPED_GMV_PRODUCTS, {
    limit: PAGE_SIZE,
    offset,
    global_vendor_id: selectedVendor?.value,
    brand_id: selectedBrand?.value,
  });
  const { data: countData, loading: countLoading } = useGet(UNMAPPED_GMV_PRODUCTS, {
    global_vendor_id: selectedVendor?.value,
    brand_id: selectedBrand?.value,
    count_only: true,
  });

  const { postData: exportUnmappedGmvProducts } = usePost(
    FILE_EXPORTS_UNMAPPED_GMV_PRODUCTS,
    () => {
      SwalToast('success', 'Export started successfully. Please check the file exports page.');
    },
  );

  const totalCount = countData?.result?.[0]?.COUNT || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleExport = () => {
    exportUnmappedGmvProducts({
      global_vendor_id: selectedVendor?.value || null,
      brand_id: selectedBrand?.value || null,
    });
  };

  return (
    <Container className={css(styles.container)}>
      <div className="d-flex justify-content-between align-items-center">
        <h3 className="font-weight-bold">Products with a sale in 1M and unmapped</h3>
        {hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
          <OverlayTrigger overlay={<Tooltip>Download CSV report</Tooltip>}>
            <Button variant="outline-primary" onClick={handleExport}>
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Export CSV
            </Button>
          </OverlayTrigger>
        )}
      </div>
      <Row>
        <Col md={3} className="mt-3">
          <SearchableScrollableSelect
            onChange={e => {
              setSelectedVendor(e);
              setCurrentPage(0);
            }}
            fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
            optionMapper={vendor => ({
              label: vendor.name,
              value: vendor.id,
            })}
            autoFocus={false}
            placeholder="Select Distributor"
          />
        </Col>
        <Col md={3} className="mt-3">
          <SearchableScrollableSelect
            onChange={e => setSelectedBrand(e)}
            fetchUrl={BRANDS_FOR_DROPDOWN}
            optionMapper={brand => ({
              label: brand.global_manufacturer_name
                ? `${brand.name} by ${brand.global_manufacturer_name}`
                : brand.name,
              value: brand.id,
            })}
            autoFocus={false}
            placeholder="Select Brand"
          />
        </Col>
        <Col md={3} className="mt-4">
          {countLoading ? (
            <Spinner animation="border" role="status" size="sm" />
          ) : (
            <Badge variant="warning">TOTAL: {totalCount.toLocaleString()}</Badge>
          )}
        </Col>
      </Row>
      <div className="d-flex justify-content-end mt-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Table striped bordered hover className="mt-4">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Name</th>
              <th>Brand Id</th>
              <th>Brand Name</th>
              <th>Distributor</th>
              <th>Monitizable GMV</th>
              <th>link</th>
            </tr>
          </thead>
          <tbody>
            {data?.result?.map(item => (
              <tr key={item.id}>
                <td>{item.VENDOR_SKU}</td>
                <td>{item.NAME}</td>
                <td>{item.BRAND_ID}</td>
                <td>{item.BRAND_NAME}</td>
                <td>{item.GLOBAL_VENDOR_NAME}</td>
                <td>{`$${Number(item.MONITIZABLE_GMV.toFixed(2)).toLocaleString('en-US')}`}</td>
                <td>
                  <Button
                    variant="link"
                    onClick={() => {
                      window.open(
                        `/global-product-detail/${item.GLOBAL_PRODUCT_ID}/${item.ID}/distributor`,
                        '_blank',
                      );
                    }}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <div className="d-flex justify-content-end mt-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '150vh',
    overflowX: 'hidden',
  },
});

export default ProductsWithASaleIn1MAndUnmappedView;
